import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";

const useInView = (threshold = 0.12) => {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) setVisible(true); }, { threshold });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);
  return [ref, visible];
};

const FadeIn = ({ children, delay = 0, style = {}, className = "" }) => {
  const [ref, visible] = useInView();
  return (
    <div ref={ref} className={className} style={{
      opacity: visible ? 1 : 0,
      transform: visible ? "translateY(0)" : "translateY(28px)",
      transition: `opacity 0.7s ease ${delay}s, transform 0.7s ease ${delay}s`,
      ...style
    }}>{children}</div>
  );
};

// ── Icônes ───────────────────────────────────────────────────
const IconHotel = ({ size = 28, color = "#C4441A" }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <rect x="2" y="7" width="20" height="15" rx="2" stroke={color} strokeWidth="1.6" fill="none"/>
    <path d="M2 10h20" stroke={color} strokeWidth="1.6"/>
    <rect x="9" y="14" width="6" height="8" rx="1" stroke={color} strokeWidth="1.4" fill="none"/>
    <rect x="4" y="12" width="3" height="3" rx="0.5" fill={color} opacity="0.6"/>
    <rect x="17" y="12" width="3" height="3" rx="0.5" fill={color} opacity="0.6"/>
    <path d="M12 2L2 7h20L12 2z" stroke={color} strokeWidth="1.6" fill="none" strokeLinejoin="round"/>
  </svg>
);
const IconFood = ({ size = 28, color = "#C4441A" }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <path d="M3 3v6a4 4 0 0 0 4 4v8" stroke={color} strokeWidth="1.6" strokeLinecap="round"/>
    <path d="M7 3v4" stroke={color} strokeWidth="1.6" strokeLinecap="round"/>
    <path d="M5 3v4" stroke={color} strokeWidth="1.6" strokeLinecap="round"/>
    <path d="M15 3a6 6 0 0 1 6 6v2h-6V3z" stroke={color} strokeWidth="1.6" strokeLinejoin="round" fill="none"/>
    <path d="M17 11v10" stroke={color} strokeWidth="1.6" strokeLinecap="round"/>
  </svg>
);
const IconGuide = ({ size = 28, color = "#C4441A" }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <circle cx="12" cy="8" r="3.5" stroke={color} strokeWidth="1.6" fill="none"/>
    <path d="M5 20c0-3.866 3.134-7 7-7s7 3.134 7 7" stroke={color} strokeWidth="1.6" strokeLinecap="round" fill="none"/>
    <path d="M19 3l1 5-3-1.5L19 3z" fill={color} opacity="0.7"/>
    <path d="M19 3v12" stroke={color} strokeWidth="1.6" strokeLinecap="round"/>
  </svg>
);
const IconTransport = ({ size = 28, color = "#C4441A" }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <rect x="2" y="7" width="20" height="12" rx="3" stroke={color} strokeWidth="1.6" fill="none"/>
    <path d="M2 11h20" stroke={color} strokeWidth="1.4"/>
    <circle cx="7" cy="19" r="2" stroke={color} strokeWidth="1.4" fill="none"/>
    <circle cx="17" cy="19" r="2" stroke={color} strokeWidth="1.4" fill="none"/>
    <path d="M7 7V5a2 2 0 0 1 2-2h6a2 2 0 0 1 2 2v2" stroke={color} strokeWidth="1.4" fill="none"/>
    <rect x="5" y="12.5" width="4" height="3" rx="0.5" fill={color} opacity="0.5"/>
    <rect x="10" y="12.5" width="4" height="3" rx="0.5" fill={color} opacity="0.5"/>
    <rect x="15" y="12.5" width="4" height="3" rx="0.5" fill={color} opacity="0.5"/>
  </svg>
);
const IconLeisure = ({ size = 28, color = "#C4441A" }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <path d="M12 2C8 2 4 6 4 10c0 6 8 12 8 12s8-6 8-12c0-4-4-8-8-8z" stroke={color} strokeWidth="1.6" fill="none"/>
    <circle cx="12" cy="10" r="3" stroke={color} strokeWidth="1.4" fill="none"/>
    <path d="M12 7v1M12 12v1M9 10H8M16 10h-1" stroke={color} strokeWidth="1.4" strokeLinecap="round"/>
  </svg>
);
const IconArtisan = ({ size = 28, color = "#C4441A" }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <path d="M12 3L4 9v12h16V9L12 3z" stroke={color} strokeWidth="1.6" strokeLinejoin="round" fill="none"/>
    <path d="M8 21v-8a4 4 0 0 1 8 0v8" stroke={color} strokeWidth="1.4" fill="none"/>
    <circle cx="12" cy="13" r="1.5" fill={color} opacity="0.7"/>
  </svg>
);
const IconPin = ({ size = 14, color = "#C4441A" }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill={color}>
    <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/>
  </svg>
);
const IconSearch = ({ size = 18, color = "#7A5A3A" }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round">
    <circle cx="11" cy="11" r="7"/><path d="M21 21l-4.35-4.35"/>
  </svg>
);
const IconShield = ({ size = 28, color = "#C4441A" }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.6" strokeLinecap="round">
    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
  </svg>
);
const IconCheck = ({ size = 28, color = "#C4441A" }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.8" strokeLinecap="round">
    <polyline points="20 6 9 17 4 12"/>
  </svg>
);
const IconWhatsapp = ({ size = 28, color = "#C4441A" }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.6" strokeLinecap="round">
    <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"/>
  </svg>
);
const IconFlag = ({ size = 28, color = "#C4441A" }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.6" strokeLinecap="round">
    <path d="M4 15s1-1 4-1 5 2 8 2 4-1 4-1V3s-1 1-4 1-5-2-8-2-4 1-4 1z"/>
    <line x1="4" y1="22" x2="4" y2="15"/>
  </svg>
);

// ── Données ──────────────────────────────────────────────────
const spots = [
  { name:"Île de Gorée", desc:"Patrimoine mondial de l'UNESCO. Un fragment d'histoire au large de Dakar.", img:"https://images.unsplash.com/photo-1547471080-7cc2caa01a7e?w=700&q=80", city:"Dakar", type:"Patrimoine" },
  { name:"Plages de Saly", desc:"Kilomètres de sable doré, eaux turquoise et resorts de rêve sur la Petite Côte.", img:"https://images.unsplash.com/photo-1507525428034-b723cf961d3e?w=700&q=80", city:"Saly", type:"Plage" },
  { name:"Casamance", desc:"Forêts tropicales, mangroves et villages traditionnels — le Sénégal sauvage.", img:"https://images.unsplash.com/photo-1516026672322-bc52d61a55d5?w=700&q=80", city:"Ziguinchor", type:"Nature" },
  { name:"Saint-Louis", desc:"Ville coloniale sur une île, jazz, culture et architecture unique au monde.", img:"https://images.unsplash.com/photo-1523906834658-6e24ef2386f9?w=700&q=80", city:"Saint-Louis", type:"Culture" },
];

const services = [
  { icon:IconHotel,    title:"Hébergements",     desc:"Appartements et villas sélectionnés — tous les budgets, toutes les villes du Sénégal.",   path:"/hotels" },
  { icon:IconFood,     title:"Restaurants",       desc:"Thiéboudienne, yassa, mafé, cuisine internationale — les meilleures tables du Sénégal.",   path:"/restaurants" },
  { icon:IconGuide,    title:"Guides Locaux",     desc:"Des guides certifiés qui connaissent chaque recoin du pays pour une expérience vraie.",    path:"/guides" },
  { icon:IconTransport,title:"Transports",        desc:"Location de voitures avec contact direct propriétaire — simple, rapide, local.",            path:"/transport" },
  { icon:IconLeisure,  title:"Loisirs & Culture", desc:"Plages, parcs naturels, musées, festivals — vivez le Sénégal dans toute sa diversité.",   path:"/loisirs" },
  { icon:IconArtisan,  title:"Artisans Locaux",   desc:"Souvenirs, textiles, sculptures — soutenez les créateurs sénégalais directement.",        path:"/artisans" },
];

const stats = [
  { val:"14",   label:"Régions du Sénégal",         sub:"Couverture nationale visée" },
 { val:"2",   label:"Villes en phase pilote",       sub:"Dakar & Saly — lancement 2026" },
  { val:"100%", label:"Sénégalais",                  sub:"Fondé & développé au Sénégal" },
  { val:"100%", label:"Acteurs locaux",               sub:"Aucune chaîne internationale" },
];

const avantages = [
  { icon: IconShield,   titre: "Vérification manuelle",   desc: "Chaque hébergement est vérifié par notre équipe avant d'apparaître sur la plateforme. Aucune surprise à l'arrivée." },
  { icon: IconCheck,    titre: "Contrat de séjour",       desc: "Pour chaque réservation, un contrat clair est envoyé au locataire. Vos droits et obligations sont protégés." },
  { icon: IconWhatsapp, titre: "Contact direct WhatsApp", desc: "Pour le transport, contactez directement le propriétaire via WhatsApp. Pas d'intermédiaire, communication locale." },
  { icon: IconFlag,     titre: "100% local & sénégalais", desc: "Plateforme fondée au Sénégal, pour les Sénégalais et les visiteurs. Chaque FCFA reste dans l'économie locale." },
];

const tabRoutes = {
  "Hébergements": "/hotels",
  "Restaurants":  "/restaurants",
  "Guides":       "/guides",
  "Transports":   "/transports",
  "Loisirs":      "/loisirs",
};

const MapSection = () => {
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  useEffect(() => {
    if (mapInstanceRef.current) return;
    if (!document.getElementById("leaflet-css")) {
      const link = document.createElement("link");
      link.id = "leaflet-css"; link.rel = "stylesheet";
      link.href = "https://unpkg.com/leaflet@1.9.4/dist/leaflet.css";
      document.head.appendChild(link);
    }
    const script = document.createElement("script");
    script.src = "https://unpkg.com/leaflet@1.9.4/dist/leaflet.js";
    script.onload = () => {
      if (!mapRef.current || mapInstanceRef.current) return;
      const L = window.L;
      const map = L.map(mapRef.current, { zoomControl:true, scrollWheelZoom:false }).setView([14.4974,-14.4524], 6);
      mapInstanceRef.current = map;
      L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", { attribution:"© OpenStreetMap", maxZoom:18 }).addTo(map);
      const markerIcon = (label) => L.divIcon({
        className:"",
        html:`<div style="background:#C4441A;color:white;padding:5px 10px;border-radius:20px;font-family:'Space Grotesk',sans-serif;font-size:12px;font-weight:600;white-space:nowrap;box-shadow:0 4px 12px rgba(196,68,26,0.4);border:2px solid white">${label}</div>`,
        iconAnchor:[40,16],
      });
      [
        { lat:14.6892, lng:-17.4467, label:"Dakar" },
        { lat:14.4632, lng:-17.0178, label:"Saly" },
        { lat:16.0200, lng:-16.4900, label:"Saint-Louis" },
        { lat:12.5500, lng:-16.2700, label:"Casamance" },
        { lat:14.6953, lng:-17.4439, label:"Gorée" },
        { lat:14.7645, lng:-17.3660, label:"Touba" },
      ].forEach(p => L.marker([p.lat,p.lng],{icon:markerIcon(p.label)}).addTo(map).bindPopup(`<b style="font-family:'Space Grotesk',sans-serif">${p.label}</b>`));
    };
    document.body.appendChild(script);
    return () => {};
  }, []);
  return <div ref={mapRef} style={{ width:"100%", height:"100%", zIndex:1 }} />;
};

export default function SenuTour() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("Hébergements");
  const [searchVal, setSearchVal] = useState("");
  const [activeSpot, setActiveSpot] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => setActiveSpot(s => (s+1) % spots.length), 4000);
    return () => clearInterval(timer);
  }, []);

  const handleSearch = () => {
    const route = tabRoutes[activeTab] || "/hotels";
    if (searchVal.trim()) {
      navigate(`${route}?search=${encodeURIComponent(searchVal.trim())}`);
    } else {
      navigate(route);
    }
  };

  const tabs = ["Hébergements", "Restaurants", "Guides", "Transports", "Loisirs"];

  return (
    <div style={{ fontFamily:"'Fraunces',serif", background:"#FAF5EC", color:"#1C1008", overflowX:"hidden" }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Fraunces:ital,opsz,wght@0,9..144,300;0,9..144,700;0,9..144,800;1,9..144,400&family=Space+Grotesk:wght@300;400;500;600;700&display=swap');
        :root { --tc:#C4441A; --tc-d:#A03614; --cream:#FAF5EC; --dark:#1C1008; --muted:#7A5A3A; }
        *{margin:0;padding:0;box-sizing:border-box} html{scroll-behavior:smooth} body{overflow-x:hidden}

        .btn-p{background:var(--tc);color:white;border:none;padding:13px 28px;border-radius:8px;font-family:'Space Grotesk',sans-serif;font-size:14px;font-weight:600;cursor:pointer;transition:all .3s;display:inline-flex;align-items:center;gap:8px}
        .btn-p:hover{background:var(--tc-d);transform:translateY(-2px);box-shadow:0 8px 20px rgba(196,68,26,.3)}
        .btn-o{background:transparent;color:var(--dark);border:1.5px solid rgba(28,16,8,.2);padding:11px 24px;border-radius:8px;font-family:'Space Grotesk',sans-serif;font-size:14px;font-weight:600;cursor:pointer;transition:all .3s}
        .btn-o:hover{background:var(--dark);color:var(--cream)}

        .search-bar{display:flex;align-items:center;background:white;border-radius:10px;padding:6px 6px 6px 18px;box-shadow:0 4px 28px rgba(28,16,8,.12);gap:10px;width:100%;border:1.5px solid rgba(28,16,8,.06)}
        .search-input{flex:1;border:none;outline:none;font-family:'Space Grotesk',sans-serif;font-size:14px;color:var(--dark);background:transparent;min-width:0}
        .tab-btn{font-family:'Space Grotesk',sans-serif;font-size:13px;font-weight:500;padding:7px 16px;border-radius:6px;border:none;cursor:pointer;transition:all .2s;white-space:nowrap}

        .service-card{background:white;border-radius:14px;padding:28px 22px;transition:all .3s;border:1.5px solid rgba(28,16,8,.06);position:relative;overflow:hidden;cursor:pointer}
        .service-card::after{content:'';position:absolute;bottom:0;left:0;right:0;height:3px;background:var(--tc);transform:scaleX(0);transition:transform .3s;transform-origin:left}
        .service-card:hover{transform:translateY(-5px);box-shadow:0 16px 40px rgba(28,16,8,.10)}
        .service-card:hover::after{transform:scaleX(1)}

        .avantage-card{background:white;border-radius:14px;padding:28px 22px;border:1.5px solid rgba(28,16,8,.06);transition:all .3s}
        .avantage-card:hover{transform:translateY(-4px);box-shadow:0 12px 32px rgba(28,16,8,.08)}

        .organic-shape{position:absolute;border-radius:50%;filter:blur(70px);pointer-events:none}

        @keyframes fadeSlide{from{opacity:0;transform:translateY(18px)}to{opacity:1;transform:translateY(0)}}
        .ha{animation:fadeSlide .9s ease forwards}
        .ha2{animation:fadeSlide .9s ease .15s forwards;opacity:0}
        .ha3{animation:fadeSlide .9s ease .3s forwards;opacity:0}
        .ha4{animation:fadeSlide .9s ease .45s forwards;opacity:0}

        @keyframes pulse{0%,100%{opacity:1}50%{opacity:.4}}
        .pulse{animation:pulse 2s ease-in-out infinite}

        @keyframes spotFade{from{opacity:0;transform:scale(1.03)}to{opacity:1;transform:scale(1)}}
        .spot-fade{animation:spotFade .6s ease forwards}

        .services-grid{display:grid;grid-template-columns:repeat(3,1fr);gap:20px}
        .avantages-grid{display:grid;grid-template-columns:repeat(4,1fr);gap:20px}
        .stats-grid{display:grid;grid-template-columns:repeat(4,1fr);gap:24px}
        .steps-grid{display:grid;grid-template-columns:repeat(3,1fr);gap:40px;position:relative}
        .spots-grid{display:grid;grid-template-columns:1fr 1fr;gap:32px;align-items:start}

        @media(max-width:1199px){
          .services-grid{grid-template-columns:repeat(2,1fr)}
          .avantages-grid{grid-template-columns:repeat(2,1fr)}
        }
        @media(max-width:767px){
          .services-grid{grid-template-columns:1fr}
          .avantages-grid{grid-template-columns:1fr}
          .stats-grid{grid-template-columns:repeat(2,1fr)}
          .steps-grid{grid-template-columns:1fr;gap:32px}
          .spots-grid{grid-template-columns:1fr}
          .hide-mobile{display:none!important}
          .hero-section{padding:100px 5% 60px!important}
          .section-pad{padding:60px 5%!important}
          .map-section{height:500px!important}
          .spot-main-img{height:280px!important}
          .cta-btns{flex-direction:column!important;align-items:stretch!important}
          .cta-btns button{width:100%!important;justify-content:center!important}
          .search-bar{flex-wrap:wrap}
          .search-bar button{width:100%;justify-content:center;border-radius:7px!important}
          .tabs-wrap{gap:6px!important}
          .tab-btn{font-size:12px!important;padding:6px 12px!important}
        }
      `}</style>

      {/* ── NAVBAR (composant unifié avec auth) ── */}
      <Navbar transparent={true} />

      {/* ── HERO ── */}
      <section className="hero-section" style={{ minHeight:"100vh",position:"relative",overflow:"hidden",display:"flex",alignItems:"center",padding:"120px 5% 80px" }}>
        <div style={{ position:"absolute",inset:0,backgroundImage:"url(https://images.unsplash.com/photo-1547471080-7cc2caa01a7e?w=1400&q=80)",backgroundSize:"cover",backgroundPosition:"center",filter:"brightness(0.45)" }} />
        <div style={{ position:"absolute",inset:0,background:"linear-gradient(135deg,rgba(28,16,8,.75) 0%,rgba(28,16,8,.3) 60%,rgba(196,68,26,.2) 100%)" }} />
        <div style={{ position:"absolute",inset:0,backgroundImage:"radial-gradient(circle,rgba(255,255,255,.04) 1px,transparent 1px)",backgroundSize:"28px 28px" }} />

        <div style={{ maxWidth:1200,margin:"0 auto",width:"100%",position:"relative",zIndex:1 }}>
          <div className="ha" style={{ display:"inline-flex",alignItems:"center",gap:8,background:"rgba(255,255,255,.1)",border:"1px solid rgba(255,255,255,.2)",borderRadius:6,padding:"6px 14px",marginBottom:24,backdropFilter:"blur(8px)" }}>
            <span className="pulse" style={{ width:7,height:7,borderRadius:"50%",background:"#4CAF50",display:"inline-block" }} />
            <span style={{ fontFamily:"'Space Grotesk',sans-serif",fontSize:12,fontWeight:600,color:"rgba(255,255,255,.9)" }}>Phase pilote active — Dakar & Saly</span>
          </div>

          <h1 className="ha2" style={{ fontSize:"clamp(32px,6vw,72px)",fontWeight:800,lineHeight:1.08,letterSpacing:"-2px",maxWidth:820,marginBottom:18,color:"white" }}>
            Tout le tourisme{" "}
            <span style={{ color:"#E8834A",fontStyle:"italic",position:"relative",display:"inline-block" }}>
              sénégalais
              <svg style={{ position:"absolute",bottom:-6,left:0,width:"100%" }} viewBox="0 0 240 10" fill="none">
                <path d="M2 7 Q60 2 120 6 Q180 10 238 5" stroke="#E8834A" strokeWidth="2.5" strokeLinecap="round" fill="none" opacity="0.6"/>
              </svg>
            </span>{" "}
            en un seul endroit
          </h1>

          <p className="ha3" style={{ fontFamily:"'Space Grotesk',sans-serif",fontSize:"clamp(14px,1.8vw,18px)",fontWeight:400,color:"rgba(255,255,255,.7)",maxWidth:520,lineHeight:1.75,marginBottom:36 }}>
            Comparez, choisissez et réservez hébergements, restaurants, guides et transports au Sénégal. Simple, local, authentique.
          </p>

          <div className="ha4">
            <div className="tabs-wrap" style={{ display:"flex",gap:8,marginBottom:14,flexWrap:"wrap" }}>
              {tabs.map(t => (
                <button key={t} className="tab-btn"
                  onClick={() => { setActiveTab(t); setSearchVal(""); }}
                  style={{ background:activeTab===t?"var(--tc)":"rgba(255,255,255,.12)",color:activeTab===t?"white":"rgba(255,255,255,.8)",backdropFilter:"blur(8px)" }}>
                  {t}
                </button>
              ))}
            </div>

            <div className="search-bar" style={{ maxWidth:580 }}>
              <IconSearch size={18} color="#B8A898" />
              <input
                className="search-input"
                placeholder={`Chercher ${activeTab === "Hébergements" ? "un hébergement" : activeTab === "Restaurants" ? "un restaurant" : activeTab === "Guides" ? "un guide" : activeTab === "Transports" ? "un transport" : "une activité"} au Sénégal...`}
                value={searchVal}
                onChange={e => setSearchVal(e.target.value)}
                onKeyDown={e => e.key === "Enter" && handleSearch()}
              />
              <button className="btn-p" style={{ padding:"11px 22px",borderRadius:7,flexShrink:0 }} onClick={handleSearch}>
                Rechercher
              </button>
            </div>

            <p style={{ fontFamily:"'Space Grotesk',sans-serif",fontSize:12,color:"rgba(255,255,255,.5)",marginTop:12 }}>
              Populaire :{" "}
              <span style={{ color:"#E8834A",cursor:"pointer" }} onClick={() => navigate("/destinations/dakar")}>Dakar</span>
              {" "}·{" "}
              <span style={{ color:"#E8834A",cursor:"pointer" }} onClick={() => navigate("/destinations/saly")}>Saly</span>
              {" "}·{" "}
              <span style={{ color:"#E8834A",cursor:"pointer" }} onClick={() => navigate("/destinations/saint-louis")}>Saint-Louis</span>
            </p>
          </div>
        </div>

        <div style={{ position:"absolute",bottom:28,left:"50%",transform:"translateX(-50%)",display:"flex",flexDirection:"column",alignItems:"center",gap:6 }}>
          <span style={{ fontFamily:"'Space Grotesk',sans-serif",fontSize:11,color:"rgba(255,255,255,.4)",letterSpacing:2,textTransform:"uppercase" }}>Défiler</span>
          <div style={{ width:1,height:28,background:"linear-gradient(to bottom,rgba(255,255,255,.4),transparent)" }} />
        </div>
      </section>

      {/* ── STATS ── */}
      <section style={{ background:"var(--tc)",padding:"48px 5%" }}>
        <div style={{ maxWidth:1200,margin:"0 auto" }}>
          <div className="stats-grid">
            {stats.map((s,i) => (
              <FadeIn key={i} delay={i*.08}>
                <div style={{ textAlign:"center" }}>
                  <div style={{ fontFamily:"'Fraunces',serif",fontSize:"clamp(28px,4vw,48px)",fontWeight:800,color:"white",lineHeight:1 }}>{s.val}</div>
                  <div style={{ fontFamily:"'Space Grotesk',sans-serif",fontSize:13,fontWeight:600,color:"rgba(255,255,255,.85)",marginTop:5 }}>{s.label}</div>
                  <div style={{ fontFamily:"'Space Grotesk',sans-serif",fontSize:11,color:"rgba(255,255,255,.5)",marginTop:2 }}>{s.sub}</div>
                </div>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      {/* ── DESTINATIONS ── */}
      <section className="section-pad" style={{ padding:"80px 5%",background:"#F0E8D4" }}>
        <div style={{ maxWidth:1200,margin:"0 auto" }}>
          <FadeIn>
            <div style={{ textAlign:"center",marginBottom:48 }}>
              <span style={{ fontFamily:"'Space Grotesk',sans-serif",fontSize:11,fontWeight:700,letterSpacing:3,color:"var(--tc)",textTransform:"uppercase",display:"block",marginBottom:12 }}>Destinations</span>
              <h2 style={{ fontFamily:"'Fraunces',serif",fontSize:"clamp(24px,4vw,44px)",fontWeight:800,letterSpacing:"-1px" }}>Le Sénégal qui vous attend</h2>
              <p style={{ fontFamily:"'Space Grotesk',sans-serif",fontSize:15,color:"var(--muted)",maxWidth:440,margin:"12px auto 0" }}>
                De Dakar à la Casamance — chaque région a ses merveilles.
              </p>
            </div>
          </FadeIn>
          <FadeIn delay={0.1}>
            <div className="spots-grid">
              <div>
                <div className="spot-main-img spot-fade" key={activeSpot} style={{ height:400,borderRadius:16,overflow:"hidden",position:"relative" }}>
                  <img src={spots[activeSpot].img} alt={spots[activeSpot].name} style={{ width:"100%",height:"100%",objectFit:"cover",display:"block" }} />
                  <div style={{ position:"absolute",inset:0,background:"linear-gradient(to top,rgba(28,16,8,.8) 0%,transparent 50%)" }} />
                  <div style={{ position:"absolute",bottom:24,left:24,right:24 }}>
                    <div style={{ display:"inline-flex",alignItems:"center",gap:6,background:"rgba(196,68,26,.85)",borderRadius:5,padding:"4px 10px",marginBottom:8 }}>
                      <span style={{ fontFamily:"'Space Grotesk',sans-serif",fontSize:11,fontWeight:600,color:"white" }}>{spots[activeSpot].type}</span>
                    </div>
                    <h3 style={{ fontFamily:"'Fraunces',serif",fontSize:"clamp(20px,3vw,28px)",fontWeight:800,color:"white",marginBottom:6,lineHeight:1.2 }}>{spots[activeSpot].name}</h3>
                    <p style={{ fontFamily:"'Space Grotesk',sans-serif",fontSize:13,color:"rgba(255,255,255,.75)",lineHeight:1.6 }}>{spots[activeSpot].desc}</p>
                    <div style={{ display:"flex",alignItems:"center",gap:5,marginTop:10 }}>
                      <IconPin size={12} color="#E8834A" />
                      <span style={{ fontFamily:"'Space Grotesk',sans-serif",fontSize:12,color:"rgba(255,255,255,.7)" }}>{spots[activeSpot].city}, Sénégal</span>
                    </div>
                  </div>
                </div>
                <div style={{ display:"flex",gap:8,marginTop:12,flexWrap:"wrap" }}>
                  {spots.map((s,i) => (
                    <div key={i} onClick={() => setActiveSpot(i)}
                      style={{ border:`2px solid ${activeSpot===i?"var(--tc)":"transparent"}`,borderRadius:8,overflow:"hidden",cursor:"pointer",flexShrink:0 }}>
                      <img src={s.img} alt={s.name} style={{ width:72,height:54,objectFit:"cover",display:"block" }} />
                    </div>
                  ))}
                </div>
              </div>
              <div style={{ display:"flex",flexDirection:"column",gap:14 }}>
                {spots.map((s,i) => (
                  <div key={i} onClick={() => setActiveSpot(i)} style={{ display:"flex",gap:14,alignItems:"center",background:"white",borderRadius:12,padding:"14px 16px",cursor:"pointer",border:`1.5px solid ${activeSpot===i?"var(--tc)":"rgba(28,16,8,.06)"}`,transition:"all .25s",boxShadow:activeSpot===i?"0 8px 24px rgba(196,68,26,.12)":"none" }}>
                    <div style={{ width:56,height:56,borderRadius:9,overflow:"hidden",flexShrink:0 }}>
                      <img src={s.img} alt={s.name} style={{ width:"100%",height:"100%",objectFit:"cover" }} />
                    </div>
                    <div style={{ flex:1,minWidth:0 }}>
                      <div style={{ display:"flex",alignItems:"center",gap:8,marginBottom:3,flexWrap:"wrap" }}>
                        <span style={{ fontFamily:"'Fraunces',serif",fontSize:15,fontWeight:700 }}>{s.name}</span>
                        <span style={{ fontFamily:"'Space Grotesk',sans-serif",fontSize:10,fontWeight:600,background:"rgba(196,68,26,.1)",color:"var(--tc)",padding:"2px 7px",borderRadius:4,flexShrink:0 }}>{s.type}</span>
                      </div>
                      <div style={{ display:"flex",alignItems:"center",gap:4 }}>
                        <IconPin size={11} color="var(--muted)" />
                        <span style={{ fontFamily:"'Space Grotesk',sans-serif",fontSize:12,color:"var(--muted)" }}>{s.city}</span>
                      </div>
                    </div>
                    <div style={{ color:"var(--tc)",fontSize:16,flexShrink:0 }}>→</div>
                  </div>
                ))}
                <button className="btn-p" style={{ width:"100%",justifyContent:"center",marginTop:4 }}
                  onClick={() => navigate("/destinations")}>
                  Explorer toutes les destinations
                </button>
              </div>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* ── SERVICES ── */}
      <section className="section-pad" style={{ padding:"80px 5%",background:"var(--cream)" }}>
        <div style={{ maxWidth:1200,margin:"0 auto" }}>
          <FadeIn>
            <div style={{ textAlign:"center",marginBottom:48 }}>
              <span style={{ fontFamily:"'Space Grotesk',sans-serif",fontSize:11,fontWeight:700,letterSpacing:3,color:"var(--tc)",textTransform:"uppercase",display:"block",marginBottom:12 }}>Nos services</span>
              <h2 style={{ fontFamily:"'Fraunces',serif",fontSize:"clamp(24px,4vw,44px)",fontWeight:800,letterSpacing:"-1px" }}>Explorer, Comparer, Réserver</h2>
            </div>
          </FadeIn>
          <div className="services-grid">
            {services.map((s,i) => (
              <FadeIn key={i} delay={i*.08}>
                <div className="service-card" onClick={() => navigate(s.path)}>
                  <div style={{ width:50,height:50,borderRadius:13,background:"rgba(196,68,26,.08)",display:"flex",alignItems:"center",justifyContent:"center",marginBottom:14 }}>
                    <s.icon size={24} color="var(--tc)" />
                  </div>
                  <h3 style={{ fontFamily:"'Fraunces',serif",fontSize:18,fontWeight:700,marginBottom:8 }}>{s.title}</h3>
                  <p style={{ fontFamily:"'Space Grotesk',sans-serif",fontSize:14,color:"var(--muted)",lineHeight:1.7 }}>{s.desc}</p>
                  <div style={{ marginTop:14,fontFamily:"'Space Grotesk',sans-serif",fontSize:13,color:"var(--tc)",fontWeight:600 }}>Explorer →</div>
                </div>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      {/* ── POURQUOI SENUTOUR ── */}
      <section className="section-pad" style={{ padding:"80px 5%",background:"#F0E8D4" }}>
        <div style={{ maxWidth:1200,margin:"0 auto" }}>
          <FadeIn>
            <div style={{ textAlign:"center",marginBottom:48 }}>
              <span style={{ fontFamily:"'Space Grotesk',sans-serif",fontSize:11,fontWeight:700,letterSpacing:3,color:"var(--tc)",textTransform:"uppercase",display:"block",marginBottom:12 }}>Pourquoi SenuTour ?</span>
              <h2 style={{ fontFamily:"'Fraunces',serif",fontSize:"clamp(24px,4vw,44px)",fontWeight:800,letterSpacing:"-1px" }}>Une plateforme pensée pour vous</h2>
              <p style={{ fontFamily:"'Space Grotesk',sans-serif",fontSize:15,color:"var(--muted)",maxWidth:480,margin:"12px auto 0",lineHeight:1.7 }}>
                Transparence, sécurité et authenticité — les valeurs qui guident chaque décision de SenuTour.
              </p>
            </div>
          </FadeIn>
          <div className="avantages-grid">
            {avantages.map((a,i) => (
              <FadeIn key={i} delay={i*.08}>
                <div className="avantage-card">
                  <div style={{ width:54,height:54,borderRadius:14,background:"rgba(196,68,26,.08)",display:"flex",alignItems:"center",justifyContent:"center",marginBottom:16 }}>
                    <a.icon size={26} color="var(--tc)" />
                  </div>
                  <h3 style={{ fontFamily:"'Fraunces',serif",fontSize:17,fontWeight:700,marginBottom:8 }}>{a.titre}</h3>
                  <p style={{ fontFamily:"'Space Grotesk',sans-serif",fontSize:13,color:"var(--muted)",lineHeight:1.7 }}>{a.desc}</p>
                </div>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      {/* ── CARTE INTERACTIVE ── */}
      <section className="map-section" style={{ position:"relative",height:600,overflow:"hidden" }}>
        <div style={{ position:"absolute",inset:0,zIndex:1 }}><MapSection /></div>
        <div style={{ position:"absolute",inset:0,zIndex:2,background:"linear-gradient(to bottom,rgba(28,16,8,.65) 0%,rgba(28,16,8,.15) 50%,rgba(28,16,8,.65) 100%)" }} />
        <div style={{ position:"relative",zIndex:3,height:"100%",display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"flex-start",padding:"72px 5% 0" }}>
          <FadeIn>
            <div style={{ textAlign:"center",marginBottom:28 }}>
              <span style={{ fontFamily:"'Space Grotesk',sans-serif",fontSize:11,fontWeight:700,letterSpacing:3,color:"rgba(255,255,255,.7)",textTransform:"uppercase",display:"block",marginBottom:12 }}>Carte interactive</span>
              <h2 style={{ fontFamily:"'Fraunces',serif",fontSize:"clamp(24px,4vw,48px)",fontWeight:800,letterSpacing:"-1px",color:"white",marginBottom:12 }}>
                Explorez le Sénégal sur la carte
              </h2>
              <p style={{ fontFamily:"'Space Grotesk',sans-serif",fontSize:14,color:"rgba(255,255,255,.65)",maxWidth:440,margin:"0 auto" }}>
                Cliquez sur un marqueur pour découvrir hébergements et activités dans chaque ville.
              </p>
            </div>
            <div style={{ display:"flex",gap:10,justifyContent:"center",flexWrap:"wrap" }}>
              {[
                { city:"Dakar",       color:"#C4441A", active:true  },
                { city:"Saly",        color:"#1A6B8B", active:true  },
                { city:"Saint-Louis", color:"#8B6914", active:false },
                { city:"Casamance",   color:"#3D6B2E", active:false },
              ].map((c,i) => (
                <div key={i} onClick={() => navigate(`/destinations/${c.city.toLowerCase()}`)}
                  style={{ display:"flex",alignItems:"center",gap:7,padding:"9px 18px",background:"rgba(28,16,8,.6)",backdropFilter:"blur(12px)",borderRadius:50,border:"1.5px solid rgba(255,255,255,.3)",cursor:"pointer",opacity:c.active?1:.65 }}>
                  <div style={{ width:8,height:8,borderRadius:"50%",background:c.color,flexShrink:0 }} />
                  <span style={{ fontFamily:"'Space Grotesk',sans-serif",fontSize:13,fontWeight:600,color:"white" }}>{c.city}</span>
                  {c.active && <span style={{ fontFamily:"'Space Grotesk',sans-serif",fontSize:10,fontWeight:700,color:"#4CAF50",background:"rgba(76,175,80,.15)",padding:"1px 6px",borderRadius:4 }}>Actif</span>}
                </div>
              ))}
            </div>
          </FadeIn>
        </div>
      </section>

      {/* ── COMMENT ÇA MARCHE ── */}
      <section id="comment-ca-marche" className="section-pad" style={{ padding:"80px 5%",background:"var(--cream)" }}>
        <div style={{ maxWidth:1100,margin:"0 auto" }}>
          <FadeIn>
            <div style={{ textAlign:"center",marginBottom:52 }}>
              <span style={{ fontFamily:"'Space Grotesk',sans-serif",fontSize:11,fontWeight:700,letterSpacing:3,color:"var(--tc)",textTransform:"uppercase",display:"block",marginBottom:12 }}>Simple comme bonjour</span>
              <h2 style={{ fontFamily:"'Fraunces',serif",fontSize:"clamp(24px,4vw,44px)",fontWeight:800,letterSpacing:"-1px" }}>Réservez en 3 étapes</h2>
            </div>
          </FadeIn>
          <div className="steps-grid">
            {[
              { step:"01", title:"Cherchez", desc:"Tapez votre destination, vos dates et le type de service. Hébergement, transport, guide — vous choisissez.", icon:(
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round">
                  <circle cx="11" cy="11" r="8"/><path d="M21 21l-4.35-4.35"/>
                </svg>
              )},
              { step:"02", title:"Comparez", desc:"Consultez les fiches détaillées, photos et prix. Notre équipe a vérifié chaque hébergement avant publication.", icon:(
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round">
                  <line x1="8" y1="6" x2="21" y2="6"/><line x1="8" y1="12" x2="21" y2="12"/><line x1="8" y1="18" x2="21" y2="18"/>
                  <line x1="3" y1="6" x2="3.01" y2="6"/><line x1="3" y1="12" x2="3.01" y2="12"/><line x1="3" y1="18" x2="3.01" y2="18"/>
                </svg>
              )},
              { step:"03", title:"Confirmez", desc:"Envoyez votre demande. Notre équipe vérifie la disponibilité et vous contacte sous 24h pour confirmer.", icon:(
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round">
                  <polyline points="20 6 9 17 4 12"/>
                </svg>
              )},
            ].map((s,i) => (
              <FadeIn key={i} delay={i*.12}>
                <div style={{ textAlign:"center",padding:"0 12px" }}>
                  <div style={{ width:60,height:60,borderRadius:"50%",background:"var(--tc)",display:"flex",alignItems:"center",justifyContent:"center",margin:"0 auto 18px",boxShadow:"0 8px 24px rgba(196,68,26,.3)" }}>
                    {s.icon}
                  </div>
                  <div style={{ fontFamily:"'Space Grotesk',sans-serif",fontSize:11,fontWeight:700,color:"var(--tc)",letterSpacing:2,textTransform:"uppercase",marginBottom:8 }}>Étape {s.step}</div>
                  <h3 style={{ fontFamily:"'Fraunces',serif",fontSize:20,fontWeight:700,marginBottom:10 }}>{s.title}</h3>
                  <p style={{ fontFamily:"'Space Grotesk',sans-serif",fontSize:14,color:"var(--muted)",lineHeight:1.7 }}>{s.desc}</p>
                </div>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA FINAL ── */}
      <section style={{ padding:"90px 5%",background:"linear-gradient(135deg,#1C1008 0%,#2D1A08 100%)",position:"relative",overflow:"hidden" }}>
        <div style={{ position:"absolute",inset:0,backgroundImage:"url(https://images.unsplash.com/photo-1507525428034-b723cf961d3e?w=1200&q=60)",backgroundSize:"cover",backgroundPosition:"center",opacity:.08 }} />
        <div className="organic-shape" style={{ width:300,height:300,background:"#C4441A",top:-60,right:-40,opacity:.15 }} />
        <div style={{ maxWidth:700,margin:"0 auto",textAlign:"center",position:"relative",zIndex:1 }}>
          <FadeIn>
            <div style={{ display:"inline-flex",alignItems:"center",gap:7,background:"rgba(255,255,255,.07)",border:"1px solid rgba(255,255,255,.12)",borderRadius:6,padding:"5px 14px",marginBottom:24 }}>
              <span className="pulse" style={{ width:6,height:6,borderRadius:"50%",background:"#4CAF50",display:"inline-block" }} />
              <span style={{ fontFamily:"'Space Grotesk',sans-serif",fontSize:12,fontWeight:600,color:"rgba(255,255,255,.7)" }}>Recherche partenaires — Phase pilote ouverte</span>
            </div>
            <h2 style={{ fontFamily:"'Fraunces',serif",fontSize:"clamp(26px,4.5vw,52px)",fontWeight:800,color:"white",lineHeight:1.12,letterSpacing:"-1.5px",marginBottom:18 }}>
              Vivez le vrai <span style={{ color:"#E8834A",fontStyle:"italic" }}>Sénégal</span>
            </h2>
            <p style={{ fontFamily:"'Space Grotesk',sans-serif",fontSize:15,color:"rgba(255,255,255,.55)",lineHeight:1.7,maxWidth:440,margin:"0 auto 36px" }}>
              Touriste ou établissement local — SenuTour est fait pour vous. Rejoignez la communauté dès maintenant.
            </p>
            <div className="cta-btns" style={{ display:"flex",gap:12,justifyContent:"center",flexWrap:"wrap" }}>
              <button className="btn-p" style={{ fontSize:15,padding:"14px 30px" }}
                onClick={() => navigate("/hotels")}>
                Trouver un hébergement
              </button>
              <button
                onClick={() => navigate("/inscription")}
                style={{ background:"transparent",border:"1.5px solid rgba(255,255,255,.22)",color:"white",padding:"14px 26px",borderRadius:8,fontFamily:"'Space Grotesk',sans-serif",fontSize:15,fontWeight:600,cursor:"pointer",transition:"all .3s" }}
                onMouseEnter={e => e.currentTarget.style.background="rgba(255,255,255,.08)"}
                onMouseLeave={e => e.currentTarget.style.background="transparent"}
              >
                Inscrire mon établissement
              </button>
            </div>
          </FadeIn>
        </div>
      </section>

      <Footer />
    </div>
  );
}